/// <amd-module name="Build/Pipelines/Actions/Favorites" />
import { Action } from "Build/Flux/Action";
import { BuildDefinition } from "Build/Client/Contracts/Build";
import { handleError } from "Build/Common/Library/ErrorHandler";

import { ArtifactScope, Favorite } from "Favorites/Common/Contracts/Favorite";
import { IFavoritesService, IFavoritesCollection } from "Favorites/Common/FavoritesService";

import { IProjectData } from 'Tfs/Platform/Page';

import { IdentityRef } from 'VSS/Platform/Contracts/WebApi';

export interface IDefinitionFavoritesActionCreatorProps {
    actionHub: DefinitionFavoritesActionHub;
    favoritesService: IFavoritesService;
    project: IProjectData;
    currentUser: IdentityRef;
}

export interface IDefinitionFavoritePayload {
    definition: IDefinitionFavoriteReference;
}

export interface IDefinitionFavoriteReference {
    uri: string,
    name: string,
    id: number
}

export class DefinitionFavoritesActionCreator {
    private _actionHub: DefinitionFavoritesActionHub;
    private _favoritesService: IFavoritesService;
    private _project: IProjectData;
    private _currentUser: IdentityRef;

    public constructor(options: IDefinitionFavoritesActionCreatorProps) {
        this._actionHub = options.actionHub;
        this._favoritesService = options.favoritesService;
        this._project = options.project;
        this._currentUser = options.currentUser;
    }

    public favoriteDefinition(definition: IDefinitionFavoriteReference) {
        let scope = {id: this._project.id, name: this._project.name, type: "Project"};

        let newFavorite: Favorite = {
            artifactId: definition.uri,
            artifactName: definition.name,
            artifactScope: scope,
            artifactType: "Microsoft.TeamFoundation.Build.Definition",
            owner: this._currentUser,
        } as Favorite;

        // Fake an immediate success and then unfavorite if the api call fails
        this._actionHub.favoriteDefinition.invoke({definition: definition});
        this._favoritesService.addFavorite(newFavorite).catch((reason: any) => {
            this._actionHub.unfavoriteDefinition.invoke({definition: definition});
            handleError(reason);
        });
    }

    public unfavoriteDefinition(definition: IDefinitionFavoriteReference) {
        let scope = {id: this._project.id, name: this._project.name, type: "Project"};

        // Fake an immediate success and then re-favorite if the api call fails
        this._actionHub.unfavoriteDefinition.invoke({definition: definition});
        this._favoritesService.getFavorites(
            "Microsoft.TeamFoundation.Build.Definition",
            scope.type,
            scope.id,
            false
        ).then((favorites: IFavoritesCollection) => {
            favorites.value.forEach((value: Favorite) => {
                if (value.artifactId === definition.uri) {
                    this._favoritesService.removeFavorite(value).catch((reason: any) => {
                        this._actionHub.favoriteDefinition.invoke({definition: definition});
                        handleError(reason);
                    });
                }
            });
        }, (reason: any) => {
            this._actionHub.favoriteDefinition.invoke({definition: definition});
            handleError(reason);
        });
    }
}

export class DefinitionFavoritesActionHub {
    private _favoriteDefinition: Action<IDefinitionFavoritePayload>;
    private _unfavoriteDefinition: Action<IDefinitionFavoritePayload>;

    constructor() {
        this._favoriteDefinition = new Action<IDefinitionFavoritePayload>();
        this._unfavoriteDefinition = new Action<IDefinitionFavoritePayload>();
    }

    public get favoriteDefinition() {
        return this._favoriteDefinition;
    }

    public get unfavoriteDefinition() {
        return this._unfavoriteDefinition;
    }
}